import { FC } from 'react';

import { PickupLocation } from 'api/api';

import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useSessionState } from '~src/global-state';

import { useCategoryState } from '../category/category-state';
import { colors } from '../theme/colors';
import { LocationAddress } from './location-address';

interface OpenModalButtonWithoutLocationProps {
  onOpen: () => any;
}
export const OpenModalButtonWithoutLocation: FC<OpenModalButtonWithoutLocationProps> = ({
  onOpen,
}) => {
  const session = useSessionState();
  const category = useCategoryState();

  const { tICU } = useTranslations();
  const { enable_address_form } = session.flags;

  if (!enable_address_form) {
    /**
     * Misconfiguration: we do not enable address form but address was not provided.
     */
    return null;
  }

  const getLocationText = () => {
    if (category.pickup_location_types && category.pickup_location_types.length === 1) {
      switch (category.pickup_location_types[0]) {
        case 'LOCKER':
          return tICU('widget.SHIPPING_CHOICE.locker.DEFAULT_LOCATION_TEXT');
        case 'STORE':
          return tICU('widget.SHIPPING_CHOICE.instore.DEFAULT_LOCATION_TEXT');
      }
    }
    return tICU(`widget.SHIPPING_CHOICE.${category.delivery_type}.DEFAULT_LOCATION_TEXT`);
  };

  return (
    <OpenModalButton
      title={{
        text: getLocationText(),
        color: colors.neutral35,
      }}
      data-test="location-select-button"
      onClick={onOpen}
      ariaLabel={tICU('widget.CHANGE_DELIVERY_LOCATION')}
    />
  );
};

interface OpenModalButtonWithLocationProps {
  onOpen: () => any;
  location: PickupLocation;
}

export const OpenModalButtonWithLocation: FC<OpenModalButtonWithLocationProps> = ({
  location,
  onOpen,
}) => {
  const { address } = location!;
  const { tICU } = useTranslations();

  return (
    <OpenModalButton
      title={{
        text: location!.name,
        textStyle: 'bold',
        color: colors.black,
      }}
      description={{
        text: <LocationAddress address={address} />,
        color: colors.neutral35,
      }}
      data-test="location-select-button"
      onClick={onOpen}
      ariaLabel={tICU('widget.CHANGE_DELIVERY_LOCATION')}
    />
  );
};

interface LocationOpenModalButtonProps {
  onOpen: () => void;
  location?: PickupLocation;
}

export const LocationOpenModalButton: FC<LocationOpenModalButtonProps> = ({ location, onOpen }) => {
  return location ? (
    <OpenModalButtonWithLocation location={location} onOpen={onOpen} />
  ) : (
    <OpenModalButtonWithoutLocation onOpen={onOpen} />
  );
};
